import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ArrowUpRight, Zap, Calendar, FileText } from 'lucide-react';

export type DemoActionType = 'booking' | 'quote' | 'contact';

interface DemoModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: DemoActionType;
}

const contentMap = {
  booking: {
    icon: Calendar,
    title: "Intelligent Scheduling",
    context: "In een productieomgeving opent hier de real-time agenda.",
    benefit: "De bezoeker kiest direct een beschikbaar tijdslot. Dit synchroniseert naadloos met uw planningsoftware, vermindert no-shows en elimineert onnodig mailverkeer.",
  },
  quote: {
    icon: FileText,
    title: "CRM Integratie",
    context: "De geconfigureerde data wordt direct verwerkt tot lead.",
    benefit: "In plaats van een platte e-mail, schiet deze flow alle specificaties (auto, budget, doel) direct in uw CRM (bijv. HubSpot). Uw sales team ontvangt een voorbereide deal.",
  },
  contact: {
    icon: Zap,
    title: "Smart Routing",
    context: "Simulatie van intelligente contact-routing.",
    benefit: "Wij sturen aanvragen direct naar de juiste afdeling op basis van de input. Zakelijk naar Sales, supportvragen naar de Werkplaats. Efficiëntie vanaf de eerste klik.",
  }
};

const DemoModal: React.FC<DemoModalProps> = ({ isOpen, onClose, type }) => {
  const content = contentMap[type];
  const Icon = content.icon;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-neutral-950/80 backdrop-blur-sm z-[60] cursor-pointer"
          />
          
          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-0 pointer-events-none flex items-center justify-center z-[70] p-4"
          >
            <div className="bg-[#0f0f0f] border border-white/10 w-full max-w-lg overflow-hidden pointer-events-auto shadow-2xl relative">
              {/* Close Button */}
              <button 
                onClick={onClose}
                className="absolute top-4 right-4 text-neutral-500 hover:text-white transition-colors p-2 z-10"
              >
                <X size={20} />
              </button>

              <div className="p-8 md:p-10">
                {/* Header Section */}
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-white/5 rounded-full flex items-center justify-center border border-white/10 text-white">
                    <Icon size={24} strokeWidth={1.5} />
                  </div>
                  <div>
                    <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-500 block mb-1">
                      Demo Concept
                    </span>
                    <h3 className="text-2xl font-display text-white">
                      {content.title}
                    </h3>
                  </div>
                </div>

                {/* Educational Content */}
                <div className="space-y-4 mb-8">
                  <p className="text-white text-sm md:text-base font-medium leading-relaxed">
                    {content.context}
                  </p>
                  <p className="text-neutral-400 text-sm leading-relaxed font-light">
                    {content.benefit}
                  </p>
                </div>

                {/* Fixels Plug */}
                <div className="bg-neutral-900/50 border border-white/5 p-4 md:p-5 mb-8">
                  <p className="text-neutral-300 text-xs md:text-sm leading-relaxed">
                    <span className="text-white font-medium">Fixels Insight:</span> Dit soort frictieloze flows verhogen conversie doorgaans met 30-50% ten opzichte van standaard formulieren.
                  </p>
                </div>

                {/* Actions */}
                <div className="flex flex-col sm:flex-row gap-3">
                   <a 
                    href="https://fixels.nl" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="flex-1 bg-white text-black hover:bg-neutral-200 transition-colors font-display uppercase tracking-widest text-xs py-4 px-6 flex items-center justify-center gap-2"
                  >
                    Bekijk Fixels <ArrowUpRight size={14} />
                  </a>
                  <button 
                    onClick={onClose}
                    className="flex-1 border border-white/10 text-neutral-400 hover:text-white hover:border-white/30 transition-colors font-display uppercase tracking-widest text-xs py-4 px-6"
                  >
                    Sluiten
                  </button>
                </div>
              </div>

              {/* Decorative line */}
              <div className="h-0.5 w-full bg-gradient-to-r from-transparent via-white/20 to-transparent absolute top-0 left-0" />
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default DemoModal;